/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useCallback, useRef, useEffect } from 'react'
import { Grid, MenuItem, TextField, Typography } from '@mui/material'
import { useDispatch } from 'react-redux'
import Theme from '@refera/ui-core'
import { Loader } from '@refera/ui-web'
import { useToast } from '_/hooks/use-toast'

import * as Modal from '_components/modal/generic-modal'
import { getServicesToImport } from '_/modules/budget/actions'
import * as yup from 'yup'

import useStyles from './styles'
import { useMessage } from '_/hooks/use-message'

export const ImportServiceModal = ({ open, handleConfirm = () => {}, handleModal = () => {} }) => {
  const styles = useStyles()
  const dispatch = useDispatch()
  const [services, setServices] = useState([])
  const [pageLoading, setPageLoading] = useState(false)
  const [serviceOrderId, setServiceOrderId] = useState(0)
  const [prevServiceOrderId, setPrevServiceOrderId] = useState(0)
  const [selectedService, setSelectedService] = useState('')
  const { showToast } = useToast()
  const abortControllerRef = useRef(null)
  const [errors, setErrors] = useState({})
  const { showMessage } = useMessage()

  // Form

  const importServiceSchema = yup.object().shape({
    serviceOrderId: yup
      .number('O ID do chamado deve ser um número válido.')
      .required('O ID do chamado é obrigatório.')
      .positive('O ID do chamado deve ser maior que zero.'),
    selectedService: yup.string().required('Selecione um serviço para importar.'),
  })
  const validateFields = async data => {
    try {
      await importServiceSchema.validate(data, { abortEarly: false })
      return { isValid: true, errors: {} }
    } catch (error1) {
      const errors1 = error1.inner.reduce((acc, error) => {
        acc[error.path] = error.message
        return acc
      }, {})
      return { isValid: false, errors: errors1 }
    }
  }

  // Effects

  useEffect(() => {
    if (open) {
      setServiceOrderId(0)
      setSelectedService('')
      setErrors({})
      setServices([])
    }
  }, [open])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  // Methods

  const fetchServices = useCallback(async () => {
    // Cancel existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()

    if (serviceOrderId > 0) {
      try {
        setPageLoading(true)
        const response = await dispatch(
          getServicesToImport({ serviceOrderId }, { signal: abortControllerRef.current.signal })
        )
        setServices(response?.services || [])
      } catch (error) {
        setServices([])
        const messageCode = error?.message_code
        if ([593, 594].includes(messageCode)) {
          showMessage(messageCode)
        }
      } finally {
        setSelectedService('')
        setPageLoading(false)
      }
    } else {
      setSelectedService('')
      setServices([])
    }
  }, [serviceOrderId, dispatch, showToast])

  const handleBlurServiceOrderId = useCallback(() => {
    if (prevServiceOrderId === serviceOrderId) {
      return
    }
    setPrevServiceOrderId(serviceOrderId)
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    fetchServices()
  }, [prevServiceOrderId, serviceOrderId, setPrevServiceOrderId, fetchServices])

  const handleValidate = async () => {
    const formData = { serviceOrderId, selectedService }
    const { isValid, errors: error1 } = await validateFields(formData)
    setErrors({})
    if (!isValid) {
      setErrors(error1)
      showToast({
        type: 'error',
        message: 'Por favor, corrija os erros antes de continuar.',
      })
    }
    return isValid
  }

  const handleImport = useCallback(async () => {
    const isValid = await handleValidate()
    if (!isValid) {
      return
    }

    const service = services.find(s => s.id === selectedService)
    if (!service) {
      showToast({
        type: 'error',
        message: 'Serviço não encontrado.',
      })
      return
    }

    handleConfirm(service)
    handleModal()
  }, [selectedService, serviceOrderId, services, handleConfirm, handleModal, showToast])

  function getServiceLabelFromId(serviceId) {
    const service = services.find(s => s.id === serviceId)
    if (service) {
      return service.label
    }
    return ''
  }

  return (
    <Grid>
      <Modal.Root open={open} onClose={() => handleModal()} maxWidth="sm" keepMounted>
        <Modal.TitleModal title="Importação de serviço" />

        <Modal.Content className={styles.modalContent}>
          <Grid className={styles.wrapper}>
            <Typography
              className={styles.label}
              style={{ color: !!errors?.serviceOrderId && Theme.Colors.Red.Base }}
            >
              ID do Chamado para importar
              <span className={styles.required}>*</span>
            </Typography>
            <TextField
              type="number"
              fullWidth
              required
              value={serviceOrderId}
              onChange={e => setServiceOrderId(e.target.value || 0)}
              onBlur={handleBlurServiceOrderId}
              inputProps={{ style: { fontSize: 14 } }}
              error={!!errors?.serviceOrderId}
              helperText={
                <Typography style={{ fontSize: 11 }}>{errors?.serviceOrderId}</Typography>
              }
            />
            <Typography
              className={styles.label}
              style={{ color: !!errors?.selectedService && Theme.Colors.Red.Base }}
            >
              Serviço para importar
              <span className={styles.required}>*</span>
            </Typography>
            <TextField
              select
              variant="standard"
              fullWidth
              required
              value={selectedService}
              onChange={e => setSelectedService(e.target.value)}
              inputProps={{ style: { fontSize: 14 } }}
              inputLabelProps={{ style: { fontSize: 14 } }}
              error={!!errors?.selectedService}
              helperText={
                <Typography style={{ fontSize: 11 }}>{errors?.selectedService}</Typography>
              }
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                    },
                  },
                },
                displayEmpty: true,
                renderValue: selectedId => {
                  return (
                    <Typography style={{ fontSize: 14 }}>
                      {getServiceLabelFromId(selectedId)}
                    </Typography>
                  )
                },
              }}
            >
              {services.map(service => (
                <MenuItem
                  key={service.id}
                  value={service.id}
                  style={{ display: 'block', fontSize: 14, padding: 4, paddingLeft: 8 }}
                >
                  {service.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Modal.Content>

        <Modal.Actions className={styles.modalActions}>
          <Modal.ButtonRed onClick={() => handleModal()}>Cancelar</Modal.ButtonRed>
          <Modal.ButtonFullBlue onClick={() => handleImport()}>Importar</Modal.ButtonFullBlue>
        </Modal.Actions>
      </Modal.Root>
      <Loader open={pageLoading} hasBackdrop />
    </Grid>
  )
}
